<template>
  <div class="bg-white p-6 dark:bg-gray-800 dark:border-gray-800 rounded-lg shadow-sm border border-gray-100">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold">Authentication Status</h3>
      <GlassSelect
        v-model="currentView"
        @change="saveViewPreference"
        :options="[
          { value: 'doughnut', label: 'Doughnut Chart' },
          { value: 'bar', label: 'Bar Chart' },
          { value: 'numbers', label: 'Numbers View' },
        ]"
        :valueKey="'value'"
        :labelKey="'label'"
        placeholder="Select View"
        class="w-auto"
      />
    </div>

    <!-- Numbers View -->
    <div v-if="currentView === 'numbers'" class="space-y-4">
      <div
        v-for="(item, index) in chartData"
        :key="index"
        class="flex items-center justify-between"
      >
        <span class="text-gray-600 dark:text-white">{{ item.name }}</span>
        <span :class="`font-medium ${item.textColor}`">{{ item.value }}</span>
      </div>
      <div class="pt-2 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <span class="text-gray-600 dark:text-white font-semibold">Total Probes</span>
          <span class="text-gray-900 darl:text-white font-semibold">{{ probeData.total }}</span>
        </div>
      </div>
    </div>

    <!-- Doughnut Chart View -->
    <div v-else-if="currentView === 'doughnut'" class="h-80">
      <Doughnut :data="doughnutData" :options="doughnutOptions" />
    </div>

    <!-- Bar Chart View -->
    <div v-else class="h-80">
      <Bar :data="barData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Cookies from "js-cookie";
import GlassSelect from "@/components/GlassSelect.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const props = defineProps({
  probeData: {
    type: Object,
    required: true,
    default: () => ({
      total: 0,
      by_status: {
        is_approved: 0,
        no_api: 0,
      },
    }),
  },
});

const currentView = ref("doughnut");
const COOKIE_NAME = "preferred-probe-auth-view";
const COOKIE_EXPIRES = 30;

onMounted(() => {
  const savedView = Cookies.get(COOKIE_NAME);
  if (savedView && ["doughnut", "bar", "numbers"].includes(savedView)) {
    currentView.value = savedView;
  }
});

const saveViewPreference = () => {
  Cookies.set(COOKIE_NAME, currentView.value, { expires: COOKIE_EXPIRES });
};

// Colors for different statuses
const colors = {
  approved: "#10B981", // emerald-500
  noApi: "#EF4444", // red-500
};

// Transform data for charts
const chartData = computed(() => [
  {
    name: "Approved",
    value: props.probeData.by_status.is_approved || 0,
    color: colors.approved,
    textColor: "text-emerald-600",
  },
  {
    name: "No API Key",
    value: props.probeData.by_status.no_api || 0,
    color: colors.noApi,
    textColor: "text-red-600",
  },
]);

// Calculate max value for y-axis scaling
const maxValue = computed(() =>
  Math.max(...chartData.value.map((item) => item.value))
);
const suggestedMax = computed(() => Math.ceil(maxValue.value * 1.2));

// Doughnut Chart Data and Options
const doughnutData = computed(() => ({
  labels: chartData.value.map((item) => item.name),
  datasets: [
    {
      data: chartData.value.map((item) => item.value),
      backgroundColor: chartData.value.map((item) => item.color),
      borderColor: "white",
      borderWidth: 2,
    },
  ],
}));

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "right",
      labels: {
        usePointStyle: true,
        padding: 20,
        font: { size: 12 },
      },
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          const total = props.probeData.total;
          const percentage = ((value / total) * 100).toFixed(1);
          return `${value} probes (${percentage}%)`;
        },
      },
    },
    datalabels: {
      color: "rgb(17 24 39)",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      font: {
        weight: "bold",
        size: 13,
      },
      formatter: (value) => {
        if (value === 0) return "";
        const percentage = ((value / props.probeData.total) * 100).toFixed(1);
        return `${percentage}%`;
      },
      textAlign: "center",
    },
  },
};

// Bar Chart Data and Options
const barData = computed(() => ({
  labels: chartData.value.map((item) => item.name),
  datasets: [
    {
      label: "Probes",
      data: chartData.value.map((item) => item.value),
      backgroundColor: chartData.value.map((item) => item.color),
      borderColor: chartData.value.map((item) => item.color),
      borderWidth: 1,
      borderRadius: 4,
      barPercentage: 0.6,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const value = context.raw;
          const total = props.probeData.total;
          const percentage = ((value / total) * 100).toFixed(1);
          return `${value} probes (${percentage}%)`;
        },
      },
    },
    datalabels: {
      anchor: "end",
      align: "top",
      offset: 4,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      color: "rgb(17 24 39)",
      font: {
        weight: "bold",
        size: 12,
      },
      formatter: (value) => value.toLocaleString(),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
      grid: {
        display: true,
      },
      suggestedMax: suggestedMax.value,
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
}));
</script>
